import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import './Footer.css';

interface LegalProduct {
  id: string;
  nameKey: string;
  nameFallback: string;
  privacy: string;
  terms: string;
}

const LEGAL_PRODUCTS: LegalProduct[] = [
  {
    id: 'bomb-world-classic',
    nameKey: 'products.bombWorldClassic.name',
    nameFallback: 'Bomb World: Classic',
    privacy: '/bomb-world-classic/privacy',
    terms: '/bomb-world-classic/terms',
  },
  {
    id: 'infinity-brain',
    nameKey: 'products.infinityBrain.name',
    nameFallback: 'Infinity Brain Training',
    privacy: '/infinity-brain/privacy',
    terms: '/infinity-brain/terms',
  },
  {
    id: 'personal-prize',
    nameKey: 'products.personalPrize.name',
    nameFallback: 'Personal Prize',
    privacy: '/personal-prize/privacy',
    terms: '/personal-prize/terms',
  },
  {
    id: 'personal-vault',
    nameKey: 'products.personalVault.name',
    nameFallback: 'Personal Vault',
    privacy: '/personal-vault/privacy',
    terms: '/personal-vault/terms',
  },
  {
    id: 'personal-contact',
    nameKey: 'products.personalContact.name',
    nameFallback: 'Personal Contact',
    privacy: '/personal-contact/privacy',
    terms: '/personal-contact/terms',
  },
  {
    id: 'wayhome',
    nameKey: 'products.wayHome.name',
    nameFallback: 'Way Home',
    privacy: '/wayhome/privacy',
    terms: '/wayhome/terms',
  },
];

function legalProductIdFromPath(pathname: string): string | null {
  return LEGAL_PRODUCTS.find((product) => pathname === product.privacy || pathname === product.terms)?.id ?? null;
}

const GitHubIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.726-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.84 1.237 1.84 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.418-1.305.76-1.605-2.665-.303-5.466-1.332-5.466-5.93 0-1.31.468-2.382 1.236-3.222-.124-.303-.535-1.523.117-3.176 0 0 1.008-.322 3.3 1.23a11.5 11.5 0 0 1 3.003-.404c1.02.005 2.047.138 3.003.404 2.29-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.873.118 3.176.77.84 1.235 1.912 1.235 3.222 0 4.61-2.807 5.624-5.48 5.92.43.372.823 1.102.823 2.222 0 1.606-.015 2.898-.015 3.293 0 .32.216.694.825.576C20.565 21.796 24 17.297 24 12 24 5.37 18.63 0 12 0z" />
  </svg>
);

const YouTubeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const InstagramIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const TikTokIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const currentYear = new Date().getFullYear();

  const [comingSoon, setComingSoon] = useState<string | null>(null);
  const [openLegalId, setOpenLegalId] = useState<string | null>(() =>
    legalProductIdFromPath(location.pathname)
  );

  useEffect(() => {
    const matched = legalProductIdFromPath(location.pathname);
    if (matched) {
      setOpenLegalId(matched);
    }
  }, [location.pathname]);

  const handleComingSoon = (name: string) => {
    setComingSoon(name);
    window.setTimeout(() => setComingSoon((current) => (current === name ? null : current)), 2500);
  };

  const placeholders = [
    { name: 'Instagram', icon: <InstagramIcon /> },
    { name: 'TikTok', icon: <TikTokIcon /> },
    { name: 'X', icon: <XIcon /> },
  ];

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="brand-logo">
              <img 
                src="/assets/snorelab_icon.png" 
                alt={t('home.title') as string} 
                className="logo-icon"
              />
              <span className="logo-text">{t('home.title')}</span>
            </div>
            <p className="footer-description">
              {t('home.subtitle')}
            </p>

            <div className="footer-social">
              <div className="footer-social-icons">
                <a
                  href="https://github.com/SynorLab"
                  className="footer-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  title="GitHub"
                >
                  <GitHubIcon />
                </a>
                <a
                  href="https://www.youtube.com/@SynorLab"
                  className="footer-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  title="YouTube"
                >
                  <YouTubeIcon />
                </a>
                {placeholders.map(({ name, icon }) => (
                  <button
                    key={name}
                    type="button"
                    className={`footer-social-link footer-social-placeholder${comingSoon === name ? ' is-active' : ''}`}
                    onClick={() => handleComingSoon(name)}
                    aria-label={`${name} – ${t('footer.comingSoon')}`}
                    title={t('footer.comingSoon') as string}
                  >
                    {icon}
                    {comingSoon === name && (
                      <span className="footer-social-tooltip">{t('footer.comingSoon')}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="footer-links">
            <div className="footer-section">
              <h4 className="footer-title">{t('footer.company')}</h4>
              <ul className="footer-list">
                <li>
                  <Link to="/about" className="footer-link">
                    {t('nav.about')}
                  </Link>
                </li>
                <li>
                  <Link to="/open-source" className="footer-link">
                    {t('nav.openSource')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('nav.contact')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4 className="footer-title">{t('footer.products')}</h4>
              <ul className="footer-list">
                <li>
                  <Link to="/products" className="footer-link">
                    {t('nav.products')}
                  </Link>
                </li>
                <li>
                  <Link to="/bomb-world-classic" className="footer-link">
                    {t('products.bombWorldClassic.name', 'Bomb World: Classic')}
                  </Link>
                </li>
                <li>
                  <Link to="/infinity-brain" className="footer-link">
                    Infinity Brain Training
                  </Link>
                </li>
                <li>
                  <Link to="/personal-prize" className="footer-link">
                    Personal Prize
                  </Link>
                </li>
                <li>
                  <Link to="/personal-vault" className="footer-link">
                    {t('products.personalVault.name', 'Personal Vault')}
                  </Link>
                </li>
                <li>
                  <Link to="/personal-contact" className="footer-link">
                    {t('products.personalContact.name', 'Personal Contact')}
                  </Link>
                </li>
                <li>
                  <Link to="/wayhome" className="footer-link">
                    Way Home
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4 className="footer-title">{t('footer.legal')}</h4>
              <ul className="footer-list">
                <li>
                  <Link to="/privacy" className="footer-link footer-link-highlight">
                    {t('footer.privacyPolicy')}
                  </Link>
                </li>
                {LEGAL_PRODUCTS.map((product) => {
                  const isOpen = openLegalId === product.id;
                  const panelId = `footer-legal-${product.id}`;
                  return (
                    <li key={product.id} className="footer-legal-item">
                      <button
                        type="button"
                        className={`footer-legal-toggle${isOpen ? ' is-open' : ''}`}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() =>
                          setOpenLegalId((current) => (current === product.id ? null : product.id))
                        }
                      >
                        <span>{t(product.nameKey, product.nameFallback)}</span>
                        <span className={`footer-legal-arrow${isOpen ? ' open' : ''}`} aria-hidden="true">
                          ▼
                        </span>
                      </button>
                      {isOpen && (
                        <ul id={panelId} className="footer-legal-docs">
                          <li>
                            <Link to={product.privacy} className="footer-link">
                              {t('footer.privacyPolicy')}
                            </Link>
                          </li>
                          <li>
                            <Link to={product.terms} className="footer-link">
                              {t('footer.termsOfService')}
                            </Link>
                          </li>
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-copyright">
            <p>© {currentYear} {t('home.title')} • {t('home.subtitle')}</p>
          </div>
          <div className="footer-contact">
            <p className="footer-email">
              {t('contact.emailContact')}: <a href="mailto:synorlab.com@gmail.com" className="footer-email-link">synorlab.com@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
